import React, { useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import RowerJourneyTimelineD3 from '../../features/dashboards/RowerJourneyTimelineD3';
import { DATA_SOURCE_CONFIG, EventMarker, Session } from '../../features/dashboards/timelineUtils';
import DashboardSelector from '../../components/coach/dashboard/DashboardSelector';
import CoachDashboard from './CoachDashboard';

const RowerJourneyPrototypePage: React.FC = () => {
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventMarker | null>(null);
  const [selectedDashboard, setSelectedDashboard] = useState<'default' | string>('default');

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    []
  );

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
    []
  );

  const handleSelectSession = (session: Session) => {
    setSelectedSession(session);
    setSelectedEvent(null);
  };

  const handleSelectEvent = (event: EventMarker) => {
    setSelectedEvent(event);
    setSelectedSession(null);
  };

  const sessionDetail = useMemo(() => {
    if (!selectedSession) return null;
    const start = new Date(selectedSession.startTime);
    const end = new Date(selectedSession.endTime);
    return {
      date: dateFormatter.format(start),
      startTime: timeFormatter.format(start),
      endTime: timeFormatter.format(end),
    };
  }, [selectedSession, dateFormatter, timeFormatter]);

  const eventDetail = useMemo(() => {
    if (!selectedEvent) return null;
    const start = new Date(selectedEvent.startDate);
    const end = selectedEvent.endDate ? new Date(selectedEvent.endDate) : start;
    const rangeLabel =
      start.getTime() === end.getTime()
        ? dateFormatter.format(start)
        : `${dateFormatter.format(start)} – ${dateFormatter.format(end)}`;
    return {
      rangeLabel,
    };
  }, [selectedEvent, dateFormatter]);

  return (
    <div className="bg-rowing-gray-50 min-h-full py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold text-rowing-navy">Rower Journey Timeline (D3)</h1>
          <p className="text-rowing-gray-600 max-w-3xl">
            Prototype view demonstrating how we can express daily training load with D3. Sessions
            are stacked by source (Peach vs RowHero), events such as regattas appear in the top
            lane, and the chart supports pan/zoom and tooltips. Data is mock for now; wiring to live
            telemetry feeds is the next step.
          </p>
        </header>

        <Card>
          <Card.Header>
            <h2 className="text-xl font-semibold text-rowing-navy">Prototype Timeline</h2>
          </Card.Header>
          <Card.Body>
            <div className="mb-4 text-sm text-rowing-gray-600">
              Hover to inspect power metrics, drag to pan, and use your trackpad or mouse wheel to
              zoom. Toggle legend chips to focus on specific sources or hide events. Select a
              session or event to preview the detail panel below.
            </div>
            <RowerJourneyTimelineD3
              onSelectSession={handleSelectSession}
              onSelectEvent={handleSelectEvent}
              selectedSessionId={selectedSession?.id ?? null}
              selectedEventId={selectedEvent?.id ?? null}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-rowing-navy">Session Dashboard</h2>
              {(selectedSession || selectedEvent) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSession(null);
                    setSelectedEvent(null);
                  }}
                  className="text-sm text-rowing-gray-500 hover:text-rowing-navy transition-colors"
                >
                  Clear Selection
                </button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {selectedSession ? (
              <>
                {/* Session Info Bar */}
                <div className="mb-4 pb-4 border-b border-rowing-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-rowing-gray-500">Session</p>
                      <p className="font-semibold text-rowing-navy">
                        {selectedSession.sessionType}
                      </p>
                    </div>
                    <div>
                      <p className="text-rowing-gray-500">Source</p>
                      <p className="font-semibold text-rowing-navy">
                        {DATA_SOURCE_CONFIG[selectedSession.dataSource]?.label ??
                          selectedSession.dataSource}
                      </p>
                    </div>
                    <div>
                      <p className="text-rowing-gray-500">Date</p>
                      <p className="font-semibold text-rowing-navy">{sessionDetail?.date}</p>
                    </div>
                    <div>
                      <p className="text-rowing-gray-500">Time</p>
                      <p className="font-semibold text-rowing-navy">
                        {sessionDetail?.startTime} – {sessionDetail?.endTime}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dashboard Dropdown */}
                <DashboardSelector
                  onSelectDashboard={(dashboardId) => {
                    setSelectedDashboard(dashboardId || 'default');
                  }}
                  viewingSavedDashboardId={
                    selectedDashboard === 'default' ? null : selectedDashboard
                  }
                />

                {/* Dashboard Content */}
                <div className="mt-6">
                  {selectedDashboard === 'default' ? (
                    <CoachDashboard selectedSessionId={selectedSession.id} />
                  ) : (
                    <div className="text-center py-8 text-rowing-gray-500">
                      <p>Custom dashboard view for: {selectedDashboard}</p>
                      <p className="text-sm mt-2">
                        Dashboard implementation coming soon for session: {selectedSession.id}
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : selectedEvent && eventDetail ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-rowing-gray-500">Event</p>
                  <p className="text-base font-semibold text-rowing-navy">{selectedEvent.label}</p>
                </div>
                <div>
                  <p className="text-sm text-rowing-gray-500">Category</p>
                  <p className="text-base font-semibold text-rowing-navy">
                    {selectedEvent.category}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-rowing-gray-500">Dates</p>
                  <p className="text-base font-semibold text-rowing-navy">
                    {eventDetail.rangeLabel}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-rowing-gray-500 text-sm">
                  Select a session on the timeline above to view detailed analytics and dashboards
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default RowerJourneyPrototypePage;
